import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { attributeValueService, referenceService } from '../services';
import { Asset, formatData, handleError, Role } from '../helpers';
import { Changelog, Icon, MainColumn, ObjectDetail, ObjectDetails, Tabs } from '../components';
import {
  useCustomProperties,
  useGlobalState,
  useNotFound,
  useMessage,
  useModalDialog,
  useSideDialog,
} from '../hooks';

export const AttributeValue = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [attributeValue, setAttributeValue] = useState([]);
  const [fromReferences, setFromReferences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loggedInUser] = useGlobalState('loggedInUser');
  const { attribute_id, attribute_value_id } = useParams();

  const { notFound, setNotFound, NotFound } = useNotFound();
  const { showMessage } = useMessage();
  const { showModalDialog, hideModalDialog } = useModalDialog();
  const { showSideDialog, hideSideDialog, setSideDialog } = useSideDialog();
  const { renderCustomPropertyValues, fetchCustomProperties } = useCustomProperties({
    objectName: Asset.AttributeValue,
    objectIds: [attribute_value_id],
  });

  useEffect(() => {
    fetchItems();
  }, [attribute_value_id]);

  const fetchItems = async () => {
    setLoading(true);
    attributeValueService
      .getById(attribute_value_id)
      .then(res => {
        if (res.attribute_values.length < 1) {
          setNotFound(true);
        } else {
          setAttributeValue(res.attribute_values[0]);
        }
      })
      .catch(err => {
        handleError({ err, showMessage, setNotFound });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchFromReferences = async () => {
    referenceService
      .getByFromId(Asset.AttributeValue, attribute_value_id)
      .then(res => {
        res && setFromReferences(res.references);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };
  useEffect(() => {
    fetchItems();
    fetchFromReferences();
  }, [attribute_value_id]);

  const deleteAttributeValue = async data => {
    await attributeValueService
      .destroy({
        attribute_values: data,
      })
      .then(res => {
        showMessage(res.message);
        hideModalDialog();
        if (location.key !== 'default') {
          // Arrived here by navigating inside Katalogue, so return the user where they came from.
          navigate(-1);
        } else {
          // Landed directly on this page — a deep link, a refresh or a new tab — so there is no
          // history to return to. Fall back to the deleted asset's parent.
          navigate(`/attributes/${attribute_id}?tab=values`);
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const editAttributeValue = async data => {
    const attributeValues = {
      attribute_values: Array.isArray(data) ? data : [data],
    };

    await attributeValueService
      .update(attributeValues)
      .then(async res => {
        await fetchItems();
        fetchCustomProperties(false);
        fetchFromReferences();
        showMessage(res.message);
        hideSideDialog();
        //@TODO: force refresh of changelog or reload page
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
    return;
  };
  const data = loading ? [] : attributeValue;

  const editable =
    loggedInUser &&
    (loggedInUser.user_role_name === Role.admin || loggedInUser.user_role_name === Role.editor);

  if (notFound) {
    return <NotFound />;
  }

  return (
    <div className="columns narrow-margin">
      <MainColumn>
        <ObjectDetails
          type="Attribute Value"
          title={
            <span>
              {data.attribute_value_name}
              {data.attribute_value_name !== data.attribute_value_code && (
                <span className="light-text">{data.attribute_value_code}</span>
              )}
            </span>
          }
          statusId={data.status_id}
          loading={loading}
          details={[
            ...renderCustomPropertyValues().details,
            {
              title: 'Last Modified',
              data: formatData(data.modified_timestamp, 'datetime'),
            },
            {
              title: 'Created',
              data: formatData(data.created_timestamp, 'datetime'),
            },
            {
              title: 'Id',
              data: data.attribute_id,
              tooltip: 'Katalogue-internal id for this asset',
            },
          ]}
          description={data.attribute_value_description}
          fromReferences={fromReferences}
          editable={editable}
          buttons={[
            {
              buttonStyle: 'main-button',
              value: 'Edit',
              onClick: () => showSideDialog('editAttributeValue', [data], editAttributeValue),
            },
            {
              value: 'Delete',
              onClick: () => showModalDialog('deleteAttributeValue', [data], deleteAttributeValue),
            },
          ]}
        >
          {data?.attribute_id && (
            <ObjectDetail label="Attribute" loading={loading} showLoadingForChildren={true}>
              <Link to={'/attributes/' + data.attribute_id} className="link">
                <Icon name={data.attribute_role_name} tooltip={data.attribute_role_name} />
                {data.attribute_name}
              </Link>
            </ObjectDetail>
          )}
          {renderCustomPropertyValues().main}
        </ObjectDetails>
      </MainColumn>
      <div className="column">
        <Tabs className="slim left">
          <div label="Changelog" tabid="changelog">
            <Changelog
              objectName={Asset.AttributeValue}
              objectId={attribute_value_id}
              filterObjects={[
                'changedData.new_data||old_data.attribute_value_name',
                'changedData.new_data||old_data.attribute_value_description',
                'changedData.new_data||old_data.attribute_value_code',
                'changed_by_user_username',
              ]}
            ></Changelog>
          </div>
        </Tabs>
      </div>
    </div>
  );
};
